<template>
  <div id="map" ref="map">
    <div v-if="aggToolTip" id="agg" class="mapboxgl-ctrl-top-right mapboxgl-ctrl">
      <div class="mapboxgl-ctrl mapboxgl-ctrl-group">
        <div class="mapboxgl-ctrl-icon" style="padding: 2px; font-size: 10px;" title="Aggregate Data"> Create Custom
          Area
          <font-awesome-icon icon="arrow-right"/>
        </div>
      </div>
    </div>
    <div v-if="deleteButton" v-on:click="deleteAll()" id="delete" class="mapboxgl-ctrl-top-right mapboxgl-ctrl">
      <div class="mapboxgl-ctrl mapboxgl-ctrl-group">
        <div class="mapboxgl-ctrl-icon" style="padding: 2px; font-size: 10px;" title="Delete All Areas"> Delete All
          <font-awesome-icon icon="trash"/>
        </div>
      </div>
    </div>
    <div id="basemap" class="mapboxgl-ctrl-top-right mapboxgl-ctrl">
      <div
          class="mapboxgl-ctrl mapboxgl-ctrl-group basemaps">
        <BaseMapSelect v-on:style-change="setMapStyle($event)"/>
      </div>
    </div>
    <div ref="mapgl" class="mapboxgl-map"></div>
    <MapboxLayer
        name="boundary"
        v-bind:geoJSON="geotype === 'schooldistricts' ? schoolboundary : boundary"
        v-bind:before-layer-name=[]
        v-bind:layers="boundaryLayer"
    />
    <MapboxLayer
        name="census_data"
        v-bind:before-layer-name=beforeLayers
        v-bind:layers="tractsStyle"
        v-bind:interactivity=canPopup
        v-on:featureClick="cartoLayer_popup($event)"
        v-bind:tiles="censusTiles"
    />
    <div class="map-overlay selectors">
      <AmenitySelect
          class="no-print"
          v-bind:mappedAmenity="mappedAmenity"
          v-bind:amenities="amenities"
          v-bind:amenityNames="amenityNames"
          v-bind:counties="counties"
          v-bind:mcd="mcdFilter"
          v-bind:isds="isds"
          v-bind:schooldistricts="schooldistricts"
          v-bind:startExpanded="startExpanded"
          v-bind:aggArea="aggArea"
          v-bind:aggIds="aggIds"
          v-bind:geoid="popupFeatureId"
          v-bind:mcdFilter="mcdFilter"
          v-bind:geotype="geotype"
          v-bind:displayType="displayType"
          v-bind:geotypes="geotypes"
          v-on:input="$emit('input', $event)"
          v-on:geotype="$emit('geotype', $event)"
          v-on:displayType="$emit('displayType', $event)"
          v-on:mcdInput="$emit('mcdInput', $event)"
          v-on:countiesInput="$emit('countiesInput', $event)"
          v-on:schoolDistrictInput="$emit('schoolDistrictInput', $event)"
          v-on:isdsInput="$emit('isdsInput', $event)"
          v-on:expandedInput="amenitySelectExpanded = $event"
      />
      <hr class="no-print" v-if="popupFeatureId">
      <DataDive
          v-if="popupFeatureId || aggArea"
          v-bind:geoid="popupFeatureId"
          v-bind:geotype="geotype"
          v-bind:geotypes="geotypes"
          v-bind:aggArea_esri_format="aggArea_esri_format"
          v-bind:censusFeatureLayerUrl="censusFeatureLayerUrl"
      />
    </div>
    <div class="map-overlay" id="legend">
      <div v-if="displayType === 'total'">{{ amenityNames[mappedAmenity].name }}</div>
      <div v-if="displayType === 'percent'">{{ amenityNames[mappedAmenity].percent_name }}</div>
      <Legend
          :colorLegendData="colorLegendData"
          :format="formatOnLegend"
          :legendMarks="legendMarks"/>
    </div>
    <div class="loader" v-if="!loaded">
      <div class="CDB-LoaderIcon CDB-LoaderIcon--big">
        <svg class="CDB-LoaderIcon-spinner" viewBox="0 0 50 50">
          <circle class="CDB-LoaderIcon-path" cx="25" cy="25" r="20" fill="none"/>
        </svg>
      </div>
    </div>
    <div v-show="false">
      <div ref="popup">
        <div v-if="popupFeatureId && idsInViewport.get(popupFeatureId)">
          <strong v-if="popupGeoname">{{popupGeoname}}</strong>
          <strong v-if="!popupGeoname">{{idsInViewport.get(popupFeatureId)['geoname']}}</strong>
          <div>{{ amenityNames[mappedAmenity].name }}:
            {{ format(idsInViewport.get(popupFeatureId)[this.amenityNames[this.mappedAmenity].total]) }}
          </div>
          <div>{{ amenityNames[mappedAmenity].percent_name }}:
            {{ formatOnPopup(idsInViewport.get(popupFeatureId)[this.amenityNames[this.mappedAmenity].percent]) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import {queryFeatures} from '@esri/arcgis-rest-feature-service';
import MapboxLayer from "./MapboxLayer";
import cartocolor from 'cartocolor'
import debounce from 'lodash.debounce'
import {naturalBreaks} from './NaturalBreaks'
import BaseMapSelect from "./BaseMapSelect";
import AmenitySelect from "./AmenitySelect";
import Legend from "./Legend";
import * as d3 from 'd3'
import * as turf from 'turf'
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import DataDive from "@/components/DataDive";

export default {
  name: "mainmap",
  components: {DataDive, MapboxLayer, BaseMapSelect, AmenitySelect, Legend},
  props: ['mcdFilter', 'counties', 'countyid', 'schooldistricts', 'isds', 'mappedAmenity', 'geotype', 'displayType'],
  data: function () {
    return {
      selectedLayers: {
        'overlays': '',
        'fillPoly': 'layer0',
      },
      sqlInViewport: "",
      idsInViewport: new Map(),
      overrideClassification: null,
      geotypes: {
        "counties": {
          "table": "counties_2020",
          "name": "Counties",
        },
        "mcds": {
          "table": "mcd_2020",
          "name": "Communities",
        },
        "isds": {
          "table": "isd_2020",
          "name": "Intermediate School Districts",
        },
        "schooldistricts": {
          "table": "schooldistrict_2020",
          "name": "School Districts",
        },
        "tracts": {
          "table": "tracts_2020",
          "name": "Census Tracts",
        },
        "blockgroups": {
          "table": "blockgroups_2020",
          "name": "Block Groups",
        },
        "blocks": {
          "table": "blocks_2020",
          "name": "Blocks",
        },
      },
      amenityNames: {
        "pop_tot": {
          "name": "Total Population",
          'percent': 'pop_acre',
          'percent_name': 'Total Population per acre',
          'total': 'pop_tot',
          "color": ['#eff3ff', '#bdd7e7', '#6baed6', '#3182bd', '#08519c'],
          "heights": [0, 5, 25, 50, 100],
          "buttonColor": '#08519c',
        },
        "age00_17": {
          "name": "Population Ages 0-17",
          'percent': 'den_0017',
          'percent_name': 'Population Ages 0-17 per acre',
          'total': 'age00_17',
          "color": ['#eff3ff', '#bdd7e7', '#6baed6', '#3182bd', '#08519c'],
          "heights": [0, 5, 25, 50, 100],
          "buttonColor": '#08519c',
        },
        "age18_up": {
          "name": "Population Ages 18+",
          'percent': 'den_18up',
          'percent_name': 'Population Ages 18+ per acre',
          'total': 'age18_up',
          "color": ['#eff3ff', '#bdd7e7', '#6baed6', '#3182bd', '#08519c'],
          "heights": [0, 5, 25, 50, 100],
          "buttonColor": '#08519c',
        },
        "hh_tot": {
          "name": "Households",
          'percent': 'hh_acre',
          'percent_name': 'Households per acre',
          'total': 'hh_tot',
          "color": ['#eff3ff', '#bdd7e7', '#6baed6', '#3182bd', '#08519c'],
          "heights": [0, 5, 25, 50, 100],
          "buttonColor": '#08519c',
        },
        "hu_tot": {
          "name": 'Housing Units',
          'percent': 'hu_acre',
          'percent_name': 'Housing Units per acre',
          'total': 'hu_tot',
          "color": ['#ffffcc', '#c2e699', '#78c679', '#31a354', '#006837'],
          "heights": [0, 5, 25, 50, 100],
          "buttonColor": cartocolor['SunsetDark'][5][4],
        },
        "hu_vac": {
          "name": 'Vacant Units',
          'percent': 'vac_rate',
          'percent_name': 'Vacancy Rate',
          'total': 'hu_vac',
          "color": ['#ffffcc', '#c2e699', '#78c679', '#31a354', '#006837'],
          "heights": [0, 5, 25, 50, 100],
          "buttonColor": cartocolor['SunsetDark'][5][4],
        }
      },
      amenities: ['pop_tot', 'age00_17', 'age18_up', 'hh_tot', 'hu_tot', 'hu_vac'],
      maxOnMap: Infinity,
      aggArea: undefined,
      aggIds: [],
      regionBoundarys: undefined,
      districtBoundarys: undefined,
      startExpanded: false,
      canPopup: true,
      aggToolTip: true,
      deleteButton: false,
      amenitySelectExpanded: this.startExpanded,
      zoomControl: new mapboxgl.NavigationControl({visualizePitch: true}),
      loaded: false,
      currentPopup: new mapboxgl.Popup({maxWidth: 'none', closeOnClick: false, className: 'tract-marker-popup'}),
      popupFeatureId: false,
    }
  },
  computed: {
    map: function () {
      mapboxgl.accessToken = 'pk.eyJ1Ijoic2VtY29nIiwiYSI6ImlvNEFUcmMifQ.VmDLZZG7l6syscFzkBs5eg'
      return new mapboxgl.Map({
        container: this.$refs.mapgl,
        style: 'mapbox://styles/semcog/cilnv2pcp005x9kly0if8ttqh',
        center: [-83.2437186609522, 42.454430721108764],
        zoom: 8.3,
        minZoom: 7,
        maxZoom: 18,
        maxBounds: [-87, 40, -79, 44],
        scrollZoom: true,
      }).addControl(new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        bbox: [-85.11, 41.55, -81.46, 43.32],
        collapsed: true,
        marker: true,
        flyTo: true,
        mapboxgl: mapboxgl,
      }).on('result', (r) => {
        this.setSelectedFromGeocode(r.result.geometry.coordinates[0], r.result.geometry.coordinates[1])
      }), 'top-right')
          .addControl(this.zoomControl, 'top-right')
          .addControl(this.draw, 'top-right');
    },
    draw: function () {
      return new MapboxDraw({
        defaultMode: 'simple_select',
        controls: {
          polygon: true,
          point: false,
          combine_features: false,
          uncombine_features: false,
          line_string: false,
          trash: false
        },
      })
    },
    beforeLayers: function () {
      return ['cartography-mcd', 'mi-county-labelpoints', 'waterway-label', 'place-village', 'mcd-pop-label-2uiaf9', 'local-dx1f7q']
    },
    boundary: function () {
      if (this.geotype === 'schooldistricts') {
        return
      }
      if (!this.regionBoundarys) {
        return this.regionBoundarys
      }
      if (Array.isArray(this.mcdFilter)) {
        return turf.union(turf.featureCollection(this.mcdFilter.map(m =>
            this.regionBoundarys[m] || this.regionBoundarys[0])))
      }
      return turf.rewind(this.regionBoundarys[this.mcdFilter || 0])
    },
    schoolboundary: function () {
      if (!this.districtBoundarys) {
        return this.districtBoundarys
      }
      if (Array.isArray(this.schooldistricts)) {
        return turf.union(turf.featureCollection(this.schooldistricts.map(m =>
            this.districtBoundarys[m] || this.districtBoundarys[0])))
      }
      return turf.rewind(this.districtBoundarys[this.schooldistricts || 0])
    },
    boundaryLayer: function () {
      return [
        {
          type: 'line',
          paint: {
            'line-width': 4,
            'line-opacity': 0.9,
            'line-color': 'black',
            'line-blur': 3
          }
        }
      ]
    },
    censusTiles: function () {
      return [`https://vectortileservices1.arcgis.com/xUx8EjNc6egUPYWh/arcgis/rest/services/${this.geotypes[this.geotype].table}/VectorTileServer/tile/{z}/{y}/{x}.pbf`]
    },
    censusFeatureLayerUrl: function () {
      return `https://services1.arcgis.com/xUx8EjNc6egUPYWh/arcgis/rest/services/${this.geotypes[this.geotype].table}/FeatureServer/0`
    },
    tractsStyle: function () {
      let line_filter = ['==', 'semgeoid', this.popupFeatureId]
      let no_pop_filter = ['>', ['get', this.amenityNames[this.mappedAmenity][this.displayType]], 0]
      let aggFilter = true
      let mcds = true
      if (this.aggIds.length > 0) {
        aggFilter = [
          "in",
          ["get", "semgeoid"],
          ["literal", this.aggIds]
        ]
      }
      if (Array.isArray(this.mcdFilter)) {
        mcds = [
          "in",
          ["get", "semmcd"],
          ["literal", this.mcdFilter]
        ]
      } else if (this.mcdFilter !== 0) {
        mcds = ['==', ["get", "semmcd"], this.mcdFilter]
      }

      if (this.geotype === 'schooldistricts') {
        if (Array.isArray(this.schooldistricts) && this.schooldistricts.length !== 0) {
          mcds = [
            "in",
            ["get", "semmcd"],
            ["literal", this.schooldistricts]
          ]
        } else if (this.schooldistricts !== 0) {
          mcds = ['==', ["get", "semmcd"], this.schooldistricts]
        }
      }
      let filter = ['all', no_pop_filter, mcds, aggFilter]

      let color = '#fff'

      let line_width = [
        'interpolate',
        ['linear'],
        ['zoom'],
        8,
        0,
        9,
        .5,
        11,
        1,
        22,
        2]

      if (this.geotype === 'blocks') {
        line_width = [
          'step',
          ['zoom'],
          0,
          12,
          0.8]
      }

      if (this.breakPoints.length) {
        color = ['step', ['get', this.amenityNames[this.mappedAmenity][this.displayType]]]

        for (let i = 0; i < this.breakPoints.length; i += 1) {
          color.push(this.colorPalette[i], this.breakPoints[i])
        }
        color.push(this.colorPalette[this.breakPoints.length])
      }
      return [
        {
          type: 'fill',
          filter: filter,
          paint: {
            'fill-color': color,
            'fill-opacity': 0.8,
          },
          "source-layer": this.geotypes[this.geotype].table
        },
        {
          type: 'fill',
          filter: line_filter,
          paint: {
            'fill-color': 'red',
            'fill-opacity': .2,
          },
          "source-layer": this.geotypes[this.geotype].table
        },
        {
          type: 'line',
          paint: {
            'line-color': '#444444',
            'line-width': line_width,
          },
          "source-layer": this.geotypes[this.geotype].table
        },
        {
          type: 'line',
          filter: line_filter,
          paint: {
            'line-opacity': 0.8,
            'line-color': 'red',
            'line-width': 3,
          },
          "source-layer": this.geotypes[this.geotype].table
        },
      ]
    },
    rawBreakPoints: function () {
      let raw_values
      let values
      if (this.selectedLayers.fillPoly === 'layer0') {
        raw_values = Array.from(this.idsInViewport.values())
            .filter(f => (
                f &&
                f[this.amenityNames[this.mappedAmenity][this.displayType]] &&
                f.landacre
            ))
        values = raw_values
            .map(f => (f[this.amenityNames[this.mappedAmenity][this.displayType]]))
      } else {
        return []
      }
      let maxvalue = Math.max(0, ...values)
      let minvalue = Math.min(Infinity, ...values)
      let count = values.length || 0
      if (count === 0 || maxvalue === 0) {
        return []
      }

      let raw = []
      let classification = this.overrideClassification;
      if (classification === 'divergingRatio') {
        raw = [
          1 - ((1 - minvalue) / 2),
          1,
          ((maxvalue - 1) / 3) + 1,
        ]
      } else if (classification === 'AreaNaturalBreaks') {
        raw = naturalBreaks(values, 4, raw_values.map(f => f.landacre))
      } else if (classification === 'AreaLogNaturalBreaks') {
        raw = naturalBreaks(values.map(Math.log1p), 4, raw_values.map(f => f.landacre)).map(Math.expm1)
      } else if (classification === 'NaturalBreaks') {
        raw = naturalBreaks(values, 4)
      } else if (classification === 'LogNaturalBreaks') {
        raw = naturalBreaks(values.map(Math.log1p), 4).map(Math.expm1)
      } else if (classification === 'AreaViewportPercentile') {
        let sorted = raw_values.sort((a, b) =>
            (a[this.amenityNames[this.mappedAmenity][this.displayType]]) -
            (b[this.amenityNames[this.mappedAmenity][this.displayType]])
        )
        let cumsumAcres = sorted.map((sum => value => sum += value.landacre)(0))
        let totalAcres = cumsumAcres[cumsumAcres.length - 1]
        let percentiles = [0.05, 0.30, 0.70, 0.95]
        raw = percentiles.map(v => {
          let i = cumsumAcres.findIndex(s => s >= (v * totalAcres)) // TODO O(n^2)
          return sorted[i][[this.amenityNames[this.mappedAmenity][this.displayType]]]
        })
      } else {
        // defeat to viewportPercentile if custom map

        let sorted = values.sort((a, b) => a - b)
        let percentiles = [0.05, 0.30, 0.70, 0.95]
        raw = percentiles.map(v => sorted[Math.floor(v * count)])
      }
      return raw
    },
    breakPoints: function () {
      let out = []
      let max = -Infinity
      this.rawBreakPoints.forEach(b => {
        if (b > max) {
          out.push(b)
          max = b
        }
      })
      return out
    },
    colorPalette: function () {
      let palette = this.amenityNames[this.mappedAmenity].color;

      let override = palette;

      if (Array.isArray(palette)) {
        override = palette;
      }

      return override || []
    },
    extrudeHeights: function () {
      return this.amenityNames[this.mappedAmenity].heights;
    },
    legendMarks: function () {
      if (!this.colorLegendData) {
        return []
      }
      let d = [0].concat(this.colorLegendData.map(legend => legend.key[1]).filter(isFinite)).concat([this.maxOnMap])
      return Array.from(new Set(d))
    },
    colorLegendData: function () {
      let out = []
      for (let i = 0; i <= this.breakPoints.length; i += 1) {
        out.push(
            {
              key: [
                this.breakPoints[i - 1] || -Infinity,
                this.breakPoints[i] || Infinity,
              ],
              value: this.colorPalette[i],
            }
        )
      }
      return out
    },
    aggArea_esri_format: function () {
      if (this.aggArea) {
        return {"rings": this.aggArea.coordinates.map((a) => a[0]), "spatialReference": {"wkid": 4326}}
      }
      return undefined
    },
    popupGeoname: function () {
      let name = ''
      if (this.geotype === 'mcds') {
        name = this.regionBoundarys[this.popupFeatureId].properties.name
      } else {
        name = undefined
      }
      return name
    }
  },
  methods: {
    setStyle: debounce(function () {
      if (this.map.getLayer('census_data_fill_0')) {
        this.$nextTick(() => {
          this.loaded = true
        })
      }
      if (!this.map.getLayer('census_data_fill_0')) {
        return
      }
      let features = this.map.queryRenderedFeatures({layers: ['census_data_fill_0']});
      // queryRenderedFeatures can return duplicates so filter them out
      features = Object.freeze(new Map(features.map(f => [f.properties.semgeoid, f.properties])));
      if (
          features.size > 0 && (
              features.size !== this.idsInViewport.size ||
              Array.from(features.keys()).some(id => !this.idsInViewport.has(id))
          )
      ) {
        this.idsInViewport = features
        this.getMaxOnMap()
      }
    }, 350, {leading: true, maxWait: 500}),
    setMapStyle: function (e) {
      this.map.setStyle(e)
    },
    getMaxOnMap: function () {
      let geoids = '1=1'

      if (this.mcdFilter && this.mcdFilter.length > 0) {
        geoids = `semgeoid IN (${this.mcdFilter.join(',')})`;
      }

      if (this.schooldistricts && this.schooldistricts.length > 0) {
        geoids = `semgeoid IN (${this.schooldistricts.join(',')})`;
      }

      queryFeatures({
        url: this.censusFeatureLayerUrl,
        where: geoids,
        outStatistics: [{
          onStatisticField: this.amenityNames[this.mappedAmenity][this.displayType],
          statisticType: 'max',
          outStatisticFieldName: 'maxonmap',
        }]
      }).then((results) => {
        this.maxOnMap = results.features[0].attributes.maxonmap
      })
    },
    getAggAreaIds: function (offset) {
      if (this.aggArea) {
        queryFeatures({
          url: this.censusFeatureLayerUrl,
          outFields: 'semgeoid',
          geometry: this.aggArea_esri_format,
          returnGeometry: false,
          resultOffset: offset,
          geometryType: "esriGeometryPolygon",
          spatialRel: "esriSpatialRelIntersects",
        }).then((results) => {
          this.aggIds = this.aggIds.concat(results.features.map(a => a.attributes.semgeoid))
          if (results.exceededTransferLimit) {
            this.getAggAreaIds(this.aggIds.length)
          }
        })
      }
    },
    formatOnLegend: function (x) {
      if (isFinite(x)) {
        let format = d3.format('.2f')(x)
        if (this.displayType === 'total') {
          format = this.format(x)
        }
        if (this.mappedAmenity === 'hu_vac' && this.displayType === 'percent')
          format = d3.format('.1%')(x)
        return format
      }
      return 'Max'
    },
    formatOnPopup: function (x) {
      let format = d3.format('.2f')(x)
      if (this.mappedAmenity === 'hu_vac' && this.displayType === 'percent')
        format = d3.format('.1%')(x)
      return format
    },
    format: d3.format(',d'),
    formatDec: d3.format('.2f'),
    fitToBoundary: function () {
      let bbox = turf.bbox(this.boundary)
      this.map.fitBounds(
          [[bbox[0], bbox[1]], [bbox[2], bbox[3]]],
      )
    },
    fitToRegion: function () {
      if (this.mcdFilter === 0) {
        let regionbbox = [
          -84.158183,
          41.723541,
          -82.418368,
          43.168951
        ]
        this.map.fitBounds(
            [[regionbbox[0], regionbbox[1]], [regionbbox[2], regionbbox[3]]],
        )
      }
    },
    fitToSchoolBoundary: function () {
      let bbox = turf.bbox(this.schoolboundary)
      this.map.fitBounds(
          [[bbox[0], bbox[1]], [bbox[2], bbox[3]]],
      )
    },
    setSelectedFromGeocode: function (x, y) {
      if (!this.map.getLayer('tracts_fill_0')) {
        return
      }
      this.map.fitBounds([-85.11, 41.55, -81.46, 43.32])
      this.map.on('zoomend', () => {
        let features = this.map.queryRenderedFeatures(this.map.project([x, y]), {layers: ['tracts_fill_0']});
        if (features.length) {
          this.popupFeatureId = features[0].properties.semgeoid
        }
      });
    },
    deleteAll: function () {
      this.draw.deleteAll()
      this.aggArea = undefined;
      this.aggIds = []
      this.popupFeatureId = false
      this.setStyle();
      this.deleteButton = false
      this.aggToolTip = true
      this.canPopup = true;
    },
    cartoLayer_popup: function (featureEvent) {
      const feature = featureEvent.features[0];
      if (!feature) {
        return;
      }
      if (this.canPopup) {
        this.currentPopup.setLngLat([featureEvent.lngLat.lng, featureEvent.lngLat.lat]).addTo(this.map);
        this.popupFeatureId = feature.properties.semgeoid;
      }

      if (this.geotype === 'counties') {
        this.$emit('countyid', this.popupFeatureId)
      }

    },
  },
  mounted: function () {
    fetch('regionBoundaries.geojson')
        .then(res => res.json())
        .then(res => {
          let regionBoundarys = {};
          regionBoundarys[0] = {
            type: 'FeatureCollection',
            features: [],
          }
          res.features.forEach(f => {
            regionBoundarys[f.properties.city_id] = f;
          });
          this.regionBoundarys = regionBoundarys;
        });
    fetch('districtBoundaries.geojson')
        .then(res => res.json())
        .then(res => {
          let boundarys = {};
          boundarys[0] = {
            type: 'FeatureCollection',
            features: [],
          }
          res.features.forEach(f => {
            boundarys[f.properties.city_id] = f;
          });
          this.districtBoundarys = boundarys;
        });
    this.currentPopup.setDOMContent(this.$refs.popup)
        .on('close', () => this.popupFeatureId = false);
    this.map.on('draw.create', () => {
      this.aggIds = []
      this.aggArea = turf.combine(this.draw.getAll()).features[0].geometry
      this.popupFeatureId = false
      this.canPopup = false;
    });
    this.map.on('draw.update', () => {
      this.aggIds = []
      this.aggArea = turf.combine(this.draw.getAll()).features[0].geometry
      this.deleteButton = true
      this.canPopup = false;
    });
    this.map.on('draw.modechange', (e) => {
      this.canPopup = false;
      this.aggToolTip = false
      this.deleteButton = e.mode === 'simple_select';
    });
    this.map.once('idle', () => {
      this.setStyle();
      this.map.on('sourcedata', (e) => {
        if (e.sourceId === 'census_data') {
          this.setStyle();
        }
      });
    });
    this.getMaxOnMap()
    this.fitToRegion()
  },
  watch: {
    boundary: function () {
      if (this.boundary && this.boundary.geometry) {
        this.fitToBoundary();
        if (!Array.isArray(this.mcdFilter) && this.mcdFilter !== 0) {
          this.popupFeatureId = this.mcdFilter
        }
      }
    },
    schoolboundary: function () {
      if (this.schoolboundary && this.schoolboundary.geometry) {
        this.fitToSchoolBoundary();
        if (!Array.isArray(this.schooldistricts) && this.schooldistricts !== 0) {
          this.popupFeatureId = this.schooldistricts
        }
      }
    },
    countyid: {
      immediate: true,
      handler: function () {
        this.popupFeatureId = this.countyid
      }
    },
    geotype: function () {
      this.popupFeatureId = false
      this.currentPopup.remove()
      this.getMaxOnMap()
      this.aggIds = []
      this.getAggAreaIds(0)
    },
    displayType: function () {
      this.getMaxOnMap()
    },
    aggArea: function () {
      this.getAggAreaIds(0)
    },
    mappedAmenity: function () {
      this.getMaxOnMap()
    },
    mcdFilter: function () {
      this.getMaxOnMap()
    }
  }
}
</script>

<style scoped>
#map {
  grid-row: 2;
  position: relative;
}

/deep/ .mapboxgl-map {
  width: 100%;
  height: 100%;
}

#legend {
  height: fit-content;
  margin-bottom: 24px;
  margin-right: 10px;
  padding: 8px;
  text-align: center;
  text-align: -webkit-center;
  text-align: -moz-center;
  color-adjust: exact;
}

#legend table {
  border-collapse: collapse;
  align-self: center;
}

/deep/ #legend th select {
  border-radius: unset;
}

/deep/ #legend th select {
  border-color: rgba(243, 243, 243, 0);
}

/deep/ #legend th select:focus {
  outline: 0;
  border: unset;
}

/deep/ #legend th select {
  font-size: 1rem;
  font-weight: bold;
}

#legend td, #legend th {
  border: 1px solid #999;
  padding: 0.2rem;
}

.text_halo {
  text-shadow: -0.05em 0 black, 0 0.05em black,
  0.05em 0 black, 0 -0.05em black;
  color: #ffffff;
}

#legend td {
  text-align: center;
  font-size: small;
  opacity: .3;
}

#legend tr td.activate {
  opacity: .95;
}

.demographic-legend span {
  padding: 2px;
}

.marker {
  opacity: .9;
  font-size: 2em;
  background-color: white;
  padding: 5px;
  background-size: cover;
  border-radius: 20%;
  cursor: pointer;
}


/deep/ .tract-marker-popup {
  opacity: .7;
}

.group {
  padding: 5px;
}

select {
  border-color: #f3f3f3;
}

select:focus {
  outline: 0;
  border-color: black;
}

select {
  border-radius: 10px;
  min-width: 0;
}

select option {
  font-size: 1rem;
}

.control-title {
  font-weight: bold;
  font-size: large;
}

@media screen and (max-width: 1000px) {
  .control-title {
    font-size: small;
    display: inline-block;
  }
}

div.selectors {
  top: 0;
  bottom: unset;
  height: fit-content;
  margin-top: 20px;
  right: auto;
  margin-left: 20px;
  padding: 8px;
}

.map-overlay {
  position: absolute;
  bottom: 0;
  right: 0;
  background: rgba(255, 255, 255, 1);
  font-family: Arial, sans-serif;
  border-radius: 3px;
}

/deep/ .popup-header {
  background-color: #011f4a;
  color: white;
  padding: 10px;
}

/deep/ .content th {
  border-bottom: 1px solid #ddd;
}

/deep/ .content td {
  border-bottom: 1px solid #ddd;
}

/deep/ .field-header {
  font-weight: bolder;
}

#basemap {
  pointer-events: none;
  position: absolute;
  margin-top: 182px;
}

#agg {
  pointer-events: none;
  position: absolute;
  margin-top: 150px;
  margin-right: 35px;
}

#delete {
  pointer-events: auto;
  cursor: pointer;
  position: absolute;
  margin-top: 150px;
  margin-right: 35px;
}

.loader {
  position: absolute;
  background: rgba(46, 60, 67, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  opacity: 1;
  transition: opacity 300ms;
  pointer-events: none;
}

.CDB-LoaderIcon--big {
  width: 40px;
  height: 40px;
}

.CDB-LoaderIcon-spinner {
  width: 40px;
  height: 40px;
  animation: rotate 2s linear infinite;
}

.CDB-LoaderIcon-path {
  stroke: rgba(255, 255, 255, .88);
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
  stroke-width: 4px
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0
  }

  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35
  }

  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124
  }
}

@keyframes rotate {
  100% {
    transform: rotate(360deg)
  }
}

@media print {
  /deep/ .mapboxgl-ctrl-geocoder, .mapboxgl-ctrl-group {
    display: none;
  }

  /deep/ select {
    appearance: none;
    border: none;
    /* needed for Firefox: */
    overflow: hidden;
  }

  /deep/ .mapboxgl-control-container {
    display: none;
  }
}

@media print {
  .no-print {
    display: none !important;
  }
}

@media screen and (max-width: 1000px) {
  #map {
    display: grid;
    grid-template-columns: auto max-content;
    grid-template-rows: auto max-content;
  }

  .map-overlay {
    margin-right: unset;
    position: unset;
  }

  div.selectors {
    margin-left: unset;
    margin-top: unset;
    grid-row: 1;
    grid-column: 2;
  }

  #basemap, #agg, #delete {
    position: relative;
    grid-area: 1 / 1;
  }

  .mapboxgl-map {
    grid-row-start: 1;
    grid-row-end: 3;
    grid-column: 1;
  }

  #legend {
    grid-row: 2;
    grid-column: 2;
    margin: 0;
  }
}

@media screen and (max-width: 600px) {
  #map {
    display: grid;
    grid-template-rows: auto max-content max-content;
    grid-template-columns: auto;
  }

  div.selectors {
    grid-row: 1;
    grid-column: 1;
  }

  .mapboxgl-map {
    grid-row: 2;
    height: 75vh;
  }

  #basemap, #agg, #delete {
    position: relative;
    grid-area: 2 / 1;
    margin-top: 155px;
  }

  #legend {
    grid-row: 3;
    grid-column: 1;
  }
}

@media screen and (max-height: 600px) {
  #map {
    display: grid;
    grid-template-columns: auto max-content;
    grid-template-rows: auto max-content;
  }

  .map-overlay {
    margin-right: unset;
    position: unset;
  }

  div.selectors {
    margin-left: unset;
    margin-top: unset;
    grid-row: 1;
    grid-column: 2;
  }

  #basemap, #agg, #delete {
    position: relative;
    grid-area: 1 / 1;
  }

  .mapboxgl-map {
    grid-row-start: 1;
    grid-row-end: 3;
    grid-column: 1;
  }

  #legend {
    grid-row: 2;
    grid-column: 2;
    margin: 0;
  }
}

</style>
